import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useCart, FREE_SHIP } from "@/context/CartContext";
import { fmtPrice } from "@/data/catalog";

export const CartLine = ({ i, compact = false }) => {
  const { setQty, remove } = useCart();
  const { i18n, t } = useTranslation();
  return (
    <div data-testid={`cart-line-${i.code}`} className="flex gap-4 py-4">
      <Link to={`/p/${i.code}`} className={`shrink-0 overflow-hidden rounded-xl bg-[#f6f7f2] ${compact ? "h-20 w-20" : "h-28 w-28"}`}>
        <img src={i.img} alt={i.name} className="h-full w-full object-contain p-2 mix-blend-multiply" />
      </Link>
      <div className="flex flex-1 flex-col">
        <span className="text-[10px] font-bold uppercase tracking-wider text-black/45">{i.brand}</span>
        <Link to={`/p/${i.code}`} className="line-clamp-2 text-sm font-semibold hover:underline">{i.name}</Link>
        <div className="mt-auto flex items-center justify-between pt-2">
          <div className="inline-flex items-center rounded-full border border-black/15">
            <button data-testid={`qty-minus-${i.code}`} onClick={() => setQty(i.code, i.qty - 1)} className="grid h-8 w-8 place-items-center rounded-full hover:bg-black hover:text-white" aria-label="-"><Minus size={12} /></button>
            <span data-testid={`qty-value-${i.code}`} className="w-8 text-center text-sm font-bold">{i.qty}</span>
            <button data-testid={`qty-plus-${i.code}`} onClick={() => setQty(i.code, i.qty + 1)} className="grid h-8 w-8 place-items-center rounded-full hover:bg-black hover:text-white" aria-label="+"><Plus size={12} /></button>
          </div>
          <div className="flex items-center gap-3">
            <span data-testid={`line-total-${i.code}`} className="font-display font-bold">{fmtPrice(i.price * i.qty, i18n.language)}</span>
            <button data-testid={`remove-${i.code}`} onClick={() => remove(i.code)} className="text-black/40 hover:text-red-600" aria-label={t("cart.remove")}><Trash2 size={16} /></button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const CartDrawer = () => {
  const { t, i18n } = useTranslation();
  const { items, count, subtotal, open, setOpen } = useCart();
  const remaining = FREE_SHIP - subtotal;
  const pct = Math.min(100, (subtotal / FREE_SHIP) * 100);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="cart-drawer" className="flex w-full flex-col p-0 sm:max-w-md">
        <SheetHeader className="border-b border-black/10 px-6 py-5">
          <SheetTitle className="font-display flex items-center gap-2 text-xl font-bold">
            <ShoppingBag size={20} /> {t("cart.title")} <span className="text-sm font-semibold text-black/50">({count})</span>
          </SheetTitle>
          <SheetDescription className="sr-only">{t("cart.title")}</SheetDescription>
        </SheetHeader>
        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
            <div className="grid h-20 w-20 place-items-center rounded-full bg-[#f4f5f0]"><ShoppingBag size={28} className="text-black/40" /></div>
            <p data-testid="cart-empty" className="font-display text-lg font-bold">{t("cart.empty")}</p>
            <p className="text-sm text-black/55">{t("cart.emptySub")}</p>
            <button onClick={() => setOpen(false)} className="nv-btn-dark mt-3">{t("cart.continue")}</button>
          </div>
        ) : (
          <>
            <div className="px-6 pt-4">
              <p className="text-xs font-semibold text-black/60">{remaining > 0 ? t("cart.freeShipHint", { amount: fmtPrice(remaining, i18n.language) }) : t("cart.freeShipOk")}</p>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-black/10"><div className="h-full rounded-full bg-[#c6ff00] transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
            </div>
            <div className="flex-1 divide-y divide-black/5 overflow-y-auto px-6">
              {items.map((i) => <CartLine key={i.code} i={i} compact />)}
            </div>
            <div className="border-t border-black/10 p-6">
              <div className="flex items-center justify-between font-display text-lg font-bold">
                <span>{t("cart.subtotal")}</span><span data-testid="cart-subtotal">{fmtPrice(subtotal, i18n.language)}</span>
              </div>
              <Link to="/checkout" onClick={() => setOpen(false)} data-testid="drawer-checkout" className="nv-btn-lime mt-4 w-full">{t("cart.checkout")} <ArrowRight size={16} /></Link>
              <Link to="/cart" onClick={() => setOpen(false)} data-testid="drawer-view-cart" className="mt-2 block text-center text-xs font-bold uppercase tracking-wider text-black/60 hover:text-black">{t("nav.cart")}</Link>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
